/**
 * M69 Map Design Laboratory — six experimental topologies.
 * Not campaign content. Not final art. Access via /dev/labs only.
 */

import type { MapDefinition, WaveDefinition } from '../model/types'
import {
  airRoute,
  emptyGrid,
  groundRoute,
  horiz,
  joinPaths,
  rowsFromGrid,
  setPath,
  vert
} from './gridPaint'

const LAB_GOLD = 500
const LAB_LIVES = 20

export type LabTopology =
  | 'single'
  | 'dual-independent'
  | 'dual-converge'
  | 'split'
  | 'triple-seams'
  | 'air-cross'

export interface LabMeta {
  id: string
  name: string
  topology: LabTopology
  purpose: string
  groundRoutes: string[]
  airRoutes: string[]
}

function buildMap(
  id: string,
  name: string,
  width: number,
  height: number,
  lanes: { id: string; cells: { x: number; y: number }[] }[],
  waves: WaveDefinition[],
  air?: { x: number; y: number }[]
): MapDefinition {
  const grid = emptyGrid(width, height)
  for (const lane of lanes) setPath(grid, lane.cells)
  const primary = lanes[0]!
  const routes = lanes.map((lane) => groundRoute(lane.id, lane.cells))
  if (air) routes.push(airRoute('air-a', air))
  return {
    schemaVersion: 1,
    id,
    name,
    width,
    height,
    rows: rowsFromGrid(grid),
    spawn: { ...primary.cells[0]! },
    exit: { ...primary.cells[primary.cells.length - 1]! },
    path: primary.cells.map((c) => ({ ...c })),
    startingGold: LAB_GOLD,
    startingLives: LAB_LIVES,
    waves,
    routes,
    ...(air
      ? {
          airRoute: airRoute('air-a', air)
        }
      : {})
  }
}

/** LAB-A — single serpentine control baseline. */
export function buildLabSingle(): MapDefinition {
  const cells = joinPaths([
    vert(4, 0, 4),
    horiz(4, 4, 14),
    vert(14, 4, 10),
    horiz(10, 14, 4),
    vert(4, 10, 16),
    horiz(16, 4, 14),
    vert(14, 16, 23)
  ])
  return buildMap(
    'lab-single',
    'Lab Single Control',
    20,
    24,
    [{ id: 'ground-a', cells }],
    [
      {
        id: 's1',
        completionReward: 40,
        groups: [{ enemy: 'normal', count: 10, interval: 0.9, delay: 0.4 }]
      },
      {
        id: 's2',
        completionReward: 45,
        groups: [{ enemy: 'fast', count: 10, interval: 0.55, delay: 0.4 }]
      },
      {
        id: 's3',
        completionReward: 50,
        groups: [{ enemy: 'tank', count: 4, interval: 1.6, delay: 0.5 }]
      },
      {
        id: 's4',
        completionReward: 55,
        groups: [
          { enemy: 'normal', count: 8, interval: 0.8, delay: 0.3 },
          { enemy: 'fast', count: 6, interval: 0.5, delay: 1.2 }
        ]
      },
      {
        id: 's5',
        completionReward: 60,
        groups: [{ enemy: 'regen', count: 5, interval: 1.2, delay: 0.4 }]
      }
    ]
  )
}

/** LAB-B — dual independent: little shared coverage. */
export function buildLabDual(): MapDefinition {
  const a = joinPaths([
    vert(2, 0, 6),
    horiz(6, 2, 6),
    vert(6, 6, 12),
    horiz(12, 6, 2),
    vert(2, 12, 18),
    horiz(18, 2, 5),
    vert(5, 18, 25)
  ])
  const b = joinPaths([
    vert(17, 0, 6),
    horiz(6, 17, 13),
    vert(13, 6, 12),
    horiz(12, 13, 17),
    vert(17, 12, 18),
    horiz(18, 17, 14),
    vert(14, 18, 25)
  ])
  return buildMap(
    'lab-dual',
    'Lab Dual Independent',
    20,
    26,
    [
      { id: 'ground-a', cells: a },
      { id: 'ground-b', cells: b }
    ],
    [
      {
        id: 'd1',
        completionReward: 40,
        groups: [{ enemy: 'normal', count: 8, interval: 1.0, delay: 0.4, routeId: 'ground-a' }]
      },
      {
        id: 'd2',
        completionReward: 40,
        groups: [{ enemy: 'fast', count: 8, interval: 0.6, delay: 0.4, routeId: 'ground-b' }]
      },
      {
        id: 'd3',
        completionReward: 55,
        groups: [
          { enemy: 'normal', count: 8, interval: 0.9, delay: 0.4, routeId: 'ground-a' },
          { enemy: 'normal', count: 8, interval: 0.9, delay: 0.4, routeId: 'ground-b' }
        ]
      },
      {
        id: 'd4',
        completionReward: 65,
        groups: [
          { enemy: 'tank', count: 3, interval: 1.8, delay: 0.5, routeId: 'ground-a' },
          { enemy: 'fast', count: 10, interval: 0.5, delay: 0.5, routeId: 'ground-b' }
        ]
      },
      {
        id: 'd5',
        completionReward: 50,
        groups: [
          { enemy: 'shield', count: 4, interval: 1.4, delay: 0.4, routeId: 'ground-a' },
          { enemy: 'regen', count: 4, interval: 1.4, delay: 0.4, routeId: 'ground-b' }
        ]
      }
    ]
  )
}

/** LAB-C — dual converge into shared trunk. */
export function buildLabConverge(): MapDefinition {
  const shared = joinPaths([vert(10, 12, 25)])
  const a = joinPaths([
    vert(3, 0, 5),
    horiz(5, 3, 10),
    vert(10, 5, 12),
    shared
  ])
  const b = joinPaths([
    vert(17, 0, 5),
    horiz(5, 17, 10),
    vert(10, 5, 12),
    shared
  ])
  return buildMap(
    'lab-converge',
    'Lab Dual Converge',
    22,
    26,
    [
      { id: 'ground-a', cells: a },
      { id: 'ground-b', cells: b }
    ],
    [
      {
        id: 'c1',
        completionReward: 40,
        groups: [
          { enemy: 'normal', count: 6, interval: 0.9, delay: 0.3, routeId: 'ground-a' },
          { enemy: 'normal', count: 6, interval: 0.9, delay: 0.3, routeId: 'ground-b' }
        ]
      },
      {
        id: 'c2',
        completionReward: 50,
        groups: [
          { enemy: 'fast', count: 8, interval: 0.5, delay: 0.2, routeId: 'ground-a' },
          { enemy: 'tank', count: 3, interval: 1.6, delay: 0.8, routeId: 'ground-b' }
        ]
      },
      {
        id: 'c3',
        completionReward: 55,
        groups: [
          { enemy: 'fast', count: 10, interval: 0.45, delay: 0.2, routeId: 'ground-b' },
          { enemy: 'normal', count: 8, interval: 0.7, delay: 0.2, routeId: 'ground-a' }
        ]
      },
      {
        id: 'c4',
        completionReward: 60,
        groups: [
          { enemy: 'regen', count: 4, interval: 1.2, delay: 0.4, routeId: 'ground-a' },
          { enemy: 'shield', count: 4, interval: 1.2, delay: 0.4, routeId: 'ground-b' }
        ]
      },
      {
        id: 'c5',
        completionReward: 70,
        groups: [
          { enemy: 'fast', count: 12, interval: 0.4, delay: 0.15, routeId: 'ground-a' },
          { enemy: 'fast', count: 12, interval: 0.4, delay: 0.15, routeId: 'ground-b' }
        ]
      }
    ]
  )
}

/** LAB-D — shared spawn trunk, two exits. */
export function buildLabSplit(): MapDefinition {
  const trunk = joinPaths([vert(10, 0, 8)])
  const a = joinPaths([trunk, horiz(8, 10, 3), vert(3, 8, 25)])
  const b = joinPaths([trunk, horiz(8, 10, 17), vert(17, 8, 25)])
  return buildMap(
    'lab-split',
    'Lab Split Two-Exit',
    22,
    26,
    [
      { id: 'ground-a', cells: a },
      { id: 'ground-b', cells: b }
    ],
    [
      {
        id: 'p1',
        completionReward: 40,
        groups: [
          { enemy: 'normal', count: 6, interval: 0.85, delay: 0.3, routeId: 'ground-a' },
          { enemy: 'normal', count: 6, interval: 0.85, delay: 0.3, routeId: 'ground-b' }
        ]
      },
      {
        id: 'p2',
        completionReward: 50,
        groups: [{ enemy: 'fast', count: 10, interval: 0.5, delay: 0.3, routeId: 'ground-a' }]
      },
      {
        id: 'p3',
        completionReward: 50,
        groups: [{ enemy: 'tank', count: 4, interval: 1.5, delay: 0.4, routeId: 'ground-b' }]
      },
      {
        id: 'p4',
        completionReward: 65,
        groups: [
          { enemy: 'fast', count: 8, interval: 0.45, delay: 0.2, routeId: 'ground-a' },
          { enemy: 'fast', count: 8, interval: 0.45, delay: 0.2, routeId: 'ground-b' }
        ]
      },
      {
        id: 'p5',
        completionReward: 70,
        groups: [
          { enemy: 'guardian', count: 2, interval: 2.0, delay: 0.5, routeId: 'ground-a' },
          { enemy: 'regen', count: 5, interval: 1.0, delay: 0.5, routeId: 'ground-b' }
        ]
      }
    ]
  )
}

/** LAB-E — triple seams, no all-three ordinary-range premium. */
export function buildLabTriple(): MapDefinition {
  const a = joinPaths([
    vert(2, 0, 5),
    horiz(5, 2, 6),
    vert(6, 5, 12),
    horiz(12, 6, 3),
    vert(3, 12, 20),
    horiz(20, 3, 6),
    vert(6, 20, 27)
  ])
  const b = joinPaths([
    vert(11, 0, 8),
    horiz(8, 11, 9),
    vert(9, 8, 16),
    horiz(16, 9, 12),
    vert(12, 16, 27)
  ])
  const c = joinPaths([
    vert(19, 0, 5),
    horiz(5, 19, 15),
    vert(15, 5, 14),
    horiz(14, 15, 18),
    vert(18, 14, 22),
    horiz(22, 18, 15),
    vert(15, 22, 27)
  ])
  return buildMap(
    'lab-triple',
    'Lab Triple Seams',
    22,
    28,
    [
      { id: 'ground-a', cells: a },
      { id: 'ground-b', cells: b },
      { id: 'ground-c', cells: c }
    ],
    [
      {
        id: 't1',
        completionReward: 40,
        groups: [{ enemy: 'normal', count: 10, interval: 0.8, delay: 0.3, routeId: 'ground-a' }]
      },
      {
        id: 't2',
        completionReward: 45,
        groups: [
          { enemy: 'tank', count: 3, interval: 1.6, delay: 0.4, routeId: 'ground-b' },
          { enemy: 'guardian', count: 1, interval: 1, delay: 1.0, routeId: 'ground-b' }
        ]
      },
      {
        id: 't3',
        completionReward: 45,
        groups: [{ enemy: 'fast', count: 12, interval: 0.45, delay: 0.3, routeId: 'ground-c' }]
      },
      {
        id: 't4',
        completionReward: 70,
        groups: [
          { enemy: 'normal', count: 6, interval: 0.7, delay: 0.2, routeId: 'ground-a' },
          { enemy: 'tank', count: 2, interval: 1.5, delay: 0.2, routeId: 'ground-b' },
          { enemy: 'fast', count: 8, interval: 0.5, delay: 0.2, routeId: 'ground-c' }
        ]
      },
      {
        id: 't5',
        completionReward: 75,
        groups: [
          { enemy: 'regen', count: 4, interval: 1.1, delay: 0.3, routeId: 'ground-a' },
          { enemy: 'shield', count: 4, interval: 1.1, delay: 0.3, routeId: 'ground-b' },
          { enemy: 'fast', count: 8, interval: 0.45, delay: 0.3, routeId: 'ground-c' }
        ]
      }
    ]
  )
}

/** LAB-F — two ground + air crossing premium band. */
export function buildLabAirCross(): MapDefinition {
  const a = joinPaths([
    vert(4, 0, 6),
    horiz(6, 4, 9),
    vert(9, 6, 14),
    horiz(14, 9, 5),
    vert(5, 14, 25)
  ])
  const b = joinPaths([
    vert(15, 0, 6),
    horiz(6, 15, 10),
    vert(10, 6, 14),
    horiz(14, 10, 14),
    vert(14, 14, 25)
  ])
  const air = [
    { x: 0, y: 24 },
    { x: 0, y: 16 },
    { x: 9, y: 16 },
    { x: 9, y: 10 },
    { x: 19, y: 10 },
    { x: 19, y: 0 }
  ]
  return buildMap(
    'lab-air-cross',
    'Lab Air Cross-Pressure',
    20,
    26,
    [
      { id: 'ground-a', cells: a },
      { id: 'ground-b', cells: b }
    ],
    [
      {
        id: 'a1',
        completionReward: 40,
        groups: [
          { enemy: 'normal', count: 6, interval: 0.9, delay: 0.3, routeId: 'ground-a' },
          { enemy: 'normal', count: 6, interval: 0.9, delay: 0.3, routeId: 'ground-b' }
        ]
      },
      {
        id: 'a2',
        completionReward: 45,
        groups: [{ enemy: 'flyer', count: 8, interval: 0.7, delay: 0.4, routeId: 'air-a' }]
      },
      {
        id: 'a3',
        completionReward: 60,
        groups: [
          { enemy: 'tank', count: 3, interval: 1.5, delay: 0.3, routeId: 'ground-a' },
          { enemy: 'fast', count: 8, interval: 0.5, delay: 0.3, routeId: 'ground-b' },
          { enemy: 'flyer', count: 6, interval: 0.65, delay: 0.5, routeId: 'air-a' }
        ]
      },
      {
        id: 'a4',
        completionReward: 55,
        groups: [
          { enemy: 'flyer', count: 10, interval: 0.55, delay: 0.2, routeId: 'air-a' },
          { enemy: 'normal', count: 8, interval: 0.8, delay: 0.2, routeId: 'ground-a' }
        ]
      },
      {
        id: 'a5',
        completionReward: 70,
        groups: [
          { enemy: 'shield', count: 3, interval: 1.3, delay: 0.3, routeId: 'ground-b' },
          { enemy: 'flyer', count: 8, interval: 0.5, delay: 0.3, routeId: 'air-a' },
          { enemy: 'fast', count: 8, interval: 0.5, delay: 0.3, routeId: 'ground-a' }
        ]
      },
      {
        id: 'a6',
        completionReward: 80,
        groups: [
          { enemy: 'air_boss', count: 1, interval: 1, delay: 0.5, routeId: 'air-a' },
          { enemy: 'tank', count: 2, interval: 1.8, delay: 0.5, routeId: 'ground-a' },
          { enemy: 'tank', count: 2, interval: 1.8, delay: 0.5, routeId: 'ground-b' }
        ]
      }
    ],
    air
  )
}

export const LAB_META: LabMeta[] = [
  {
    id: 'lab-single',
    name: 'Lab Single Control',
    topology: 'single',
    purpose: 'Baseline single-lane control for topology A/B comparisons.',
    groundRoutes: ['ground-a'],
    airRoutes: []
  },
  {
    id: 'lab-dual',
    name: 'Lab Dual Independent',
    topology: 'dual-independent',
    purpose: 'Forced distribution — little shared ordinary-range coverage.',
    groundRoutes: ['ground-a', 'ground-b'],
    airRoutes: []
  },
  {
    id: 'lab-converge',
    name: 'Lab Dual Converge',
    topology: 'dual-converge',
    purpose: 'Early split defense vs late shared kill zone.',
    groundRoutes: ['ground-a', 'ground-b'],
    airRoutes: []
  },
  {
    id: 'lab-split',
    name: 'Lab Split Two-Exit',
    topology: 'split',
    purpose: 'Pre-split coverage vs specialized post-split defense.',
    groundRoutes: ['ground-a', 'ground-b'],
    airRoutes: []
  },
  {
    id: 'lab-triple',
    name: 'Lab Triple Seams',
    topology: 'triple-seams',
    purpose: 'Pairwise seams only — no all-three ordinary-range premium.',
    groundRoutes: ['ground-a', 'ground-b', 'ground-c'],
    airRoutes: []
  },
  {
    id: 'lab-air-cross',
    name: 'Lab Air Cross-Pressure',
    topology: 'air-cross',
    purpose: 'AA placement competes with ground premiums.',
    groundRoutes: ['ground-a', 'ground-b'],
    airRoutes: ['air-a']
  }
]

export const LAB_BUILDERS: Record<string, () => MapDefinition> = {
  'lab-single': buildLabSingle,
  'lab-dual': buildLabDual,
  'lab-converge': buildLabConverge,
  'lab-split': buildLabSplit,
  'lab-triple': buildLabTriple,
  'lab-air-cross': buildLabAirCross
}

export function getLabMap(id: string): MapDefinition | undefined {
  const build = LAB_BUILDERS[id]
  return build ? build() : undefined
}

export function listLabMaps(): MapDefinition[] {
  return LAB_META.map((m) => LAB_BUILDERS[m.id]!())
}
